import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import TextArea from '@/common/components/TextArea'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCancelContractEnvelope } from '@/features/privateCompanyLeadsFunnel/hooks/useCancelContractEnvelope'
import {
  cancelContractEnvelopeSchema,
  CancelContractEnvelopeValidationSchema,
} from '@/features/privateCompanyLeadsFunnel/schemas/contractEnvelope.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

interface ModalCancelContractEnvelopeProps {
  privateCompanyLeadId: string
  contractEnvelopeStatus?: string
  setMessages: Dispatch<SetStateAction<string[] | boolean>>
}

export const ModalCancelContractEnvelope = ({
  privateCompanyLeadId,
  contractEnvelopeStatus,
  setMessages,
}: ModalCancelContractEnvelopeProps) => {
  const queryClient = useQueryClient()

  const { getUserPermission } = useUserPermissions()

  const [isOpen, setIsOpen] = useState(false)

  const userHasPermissionToCancelRemoteSignature = getUserPermission(
    'Admin::Funnel::PrivateCompanyLeads::CancelRemoteSignature',
  )?.properties?.permitted_states?.includes('quoting_finalization')

  const {
    register,
    reset,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<CancelContractEnvelopeValidationSchema>({
    resolver: zodResolver(cancelContractEnvelopeSchema),
  })

  const onSuccess = () => {
    setIsOpen(false)
    reset()
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLead', privateCompanyLeadId] })
    queryClient.invalidateQueries({ queryKey: ['privateCompanyLeadContractEnvelope', privateCompanyLeadId] })
  }

  const { mutate: cancelContractEnvelope, isPending: cancelContractEnvelopeIsPending } = useCancelContractEnvelope({
    id: privateCompanyLeadId,
    onSuccess,
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<CancelContractEnvelopeValidationSchema> = (data) => {
    cancelContractEnvelope(data)
  }

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      Trigger={
        <Button
          type="button"
          disabled={!userHasPermissionToCancelRemoteSignature}
          tooltipMessage={!userHasPermissionToCancelRemoteSignature ? 'Você não tem permissão' : undefined}
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-x-2 bg-danger-op hover:bg-danger disabled:hover:bg-danger-op"
        >
          <p className="text-sm">
            {contractEnvelopeStatus === 'signed' ? 'Cancelar Contrato' : 'Cancelar assinaturas'}
          </p>

          <XIcon className="size-4" />
        </Button>
      }
      useCancellableFooter
      showClose={false}
      title="Você tem certeza?"
      subtitle="Esse contrato e suas assinaturas serão canceladas, você poderá reiniciar o envio por sistema ou assinar externamente."
      titleClass="text-2xl"
      subtitleClassName="mt-1 text-sm"
      confirmButtonText="Cancelar contrato"
      cancelButtonClassName="text-sm"
      confirmButtonClassName="text-sm bg-primary-blue"
      confirmButtonIsLoading={cancelContractEnvelopeIsPending}
      onCancel={() => setIsOpen(false)}
      onConfirm={handleSubmit(onSubmit)}
      body={
        <form className="text-white-800">
          <TextArea
            label="Justificativa"
            placeholder="Escreva aqui sua justificativa"
            markAsRequired
            {...register('invalidating_reason')}
            errorMessage={errors.invalidating_reason?.message}
            variant={errors.invalidating_reason?.message ? 'error' : 'default'}
          />
        </form>
      }
    />
  )
}
